import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Filter, X } from "lucide-react";

function getNextGroupValues(currentValues, optionValue) {
  const current = Array.isArray(currentValues) ? currentValues.map(String) : [];
  const normalizedValue = String(optionValue);

  if (current.includes(normalizedValue)) {
    return current.filter((item) => item !== normalizedValue);
  }

  return [...current, normalizedValue];
}

function toStringArray(value) {
  return Array.isArray(value) ? value.map(String) : [];
}

function hasEveryDetailOptionSelected(selection, groups, allOptionGroupKey) {
  const detailGroups = groups.filter(
    (group) => String(group.key) !== allOptionGroupKey,
  );
  const detailOptions = detailGroups.flatMap((group) =>
    (group.options ?? []).map((option) => ({
      groupKey: group.key,
      optionValue: String(option.value),
    })),
  );

  if (!detailOptions.length) return false;

  return detailOptions.every(({ groupKey, optionValue }) =>
    toStringArray(selection?.[groupKey]).includes(optionValue),
  );
}

function getAllOptionOnlyValue(value, groups, allOptionGroupKey, allOptionValue) {
  const nextValue = { ...value };

  groups.forEach((group) => {
    nextValue[group.key] = [];
  });

  nextValue[allOptionGroupKey] = [allOptionValue];
  return nextValue;
}

export default function DetailConditionFilterDropdown({
  label,
  groups = [],
  value = {},
  onChange,
  onClear,
  activeCount = 0,
  activeText = "",
  clearLabel = "Clear",
  applyLabel = "Apply",
  allOptionGroupKey = "",
  allOptionValue = "",
  syncAllOption = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) {
      setDraftValue(value);
    }
  }, [isOpen, value]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isOpen]);

  const handleToggleOpen = () => {
    setIsOpen((current) => {
      const nextOpen = !current;
      if (nextOpen) {
        setDraftValue(value);
      }
      return nextOpen;
    });
  };

  const handleToggleValue = (groupKey, optionValue) => {
    const normalizedGroupKey = String(groupKey);
    const normalizedOptionValue = String(optionValue);
    const normalizedAllGroupKey = String(allOptionGroupKey);
    const normalizedAllValue = String(allOptionValue);
    const shouldSyncAllOption = Boolean(
      syncAllOption && normalizedAllGroupKey && normalizedAllValue,
    );
    const isAllOption =
      shouldSyncAllOption &&
      normalizedGroupKey === normalizedAllGroupKey &&
      normalizedOptionValue === normalizedAllValue;

    if (isAllOption) {
      const isSelected = toStringArray(draftValue?.[groupKey]).includes(
        normalizedAllValue,
      );

      setDraftValue(
        isSelected
          ? {
              ...draftValue,
              [groupKey]: [],
            }
          : getAllOptionOnlyValue(
              draftValue,
              groups,
              normalizedAllGroupKey,
              normalizedAllValue,
            ),
      );
      return;
    }

    let nextValue = {
      ...draftValue,
      [groupKey]: getNextGroupValues(draftValue?.[groupKey], optionValue),
    };

    if (shouldSyncAllOption) {
      nextValue[normalizedAllGroupKey] = [];

      if (
        hasEveryDetailOptionSelected(
          nextValue,
          groups,
          normalizedAllGroupKey,
        )
      ) {
        nextValue = getAllOptionOnlyValue(
          nextValue,
          groups,
          normalizedAllGroupKey,
          normalizedAllValue,
        );
      }
    }

    setDraftValue(nextValue);
  };

  const handleClear = () => {
    onClear?.();
    setIsOpen(false);
  };

  const handleApply = () => {
    onChange?.(draftValue);
    setIsOpen(false);
  };

  return (
    <div className="detailConditionFilter" ref={rootRef}>
      <button
        aria-controls={menuId}
        aria-expanded={isOpen}
        className={`detailConditionFilter__trigger ${
          activeCount > 0 ? "isActive" : ""
        }`}
        onClick={handleToggleOpen}
        type="button"
      >
        <Filter size={15} strokeWidth={2.2} aria-hidden="true" />
        <span>{label}</span>
        {activeCount > 0 ? (
          <em>{activeText || activeCount}</em>
        ) : null}
        <ChevronDown size={15} strokeWidth={2.2} aria-hidden="true" />
      </button>

      {isOpen ? (
        <div className="detailConditionFilter__menu" id={menuId}>
          <div className="detailConditionFilter__groups">
            {groups.map((group) => (
              <section
                className="detailConditionFilter__group"
                key={group.key}
              >
                <strong>{group.label}</strong>
                <div className="detailConditionFilter__options">
                  {(group.options ?? []).map((option) => {
                    const optionValue = String(option.value);
                    const checked = Array.isArray(draftValue?.[group.key])
                      ? draftValue[group.key].map(String).includes(optionValue)
                      : false;

                    return (
                      <button
                        aria-pressed={checked}
                        className={`detailConditionFilter__option ${
                          checked ? "isChecked" : ""
                        }`}
                        key={`${group.key}-${optionValue}`}
                        onClick={() =>
                          handleToggleValue(group.key, optionValue)
                        }
                        type="button"
                      >
                        <span>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <div className="detailConditionFilter__footer">
            <button
              className="detailConditionFilter__clear"
              disabled={activeCount <= 0}
              onClick={handleClear}
              type="button"
            >
              <X size={14} strokeWidth={2.2} aria-hidden="true" />
              <span>{clearLabel}</span>
            </button>
            <button
              className="detailConditionFilter__apply"
              onClick={handleApply}
              type="button"
            >
              {applyLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
